import type { MelodyTrainerStats } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './melody-trainer-summary.module.css'

/**
 * End-of-session summary: how close the notes were, and which way the playing leaned.
 *
 * The tendency uses the SAME three lexicon entries as the per-attempt verdict, so the summary
 * cannot phrase the session's lean in words the player has not already seen attached to a number.
 * When no attempt produced a timing measurement there is no tendency line at all - "even" would be
 * a claim about playing that never happened.
 *
 * Attempts nobody performed are excluded from the mean rather than counted as zero, and when that
 * happened the count is shown: a percentage of three attempts is not a percentage of five, and
 * quietly presenting it as one would flatter a broken microphone.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6, §10
 */

export interface MelodyTrainerSummaryProps {
  stats: MelodyTrainerStats
  onRestart: () => void
  /** Present when the trainer is a dialog over a melody rather than a page. */
  onClose?: () => void
}

export const MelodyTrainerSummary: FC<MelodyTrainerSummaryProps> = props => {
  const { stats, onRestart, onClose } = props
  const tendency = useVocalCopy(stats.tendency === null ? '' : `timing-${stats.tendency}`, {
    ms: stats.meanErrorMs === null ? undefined : Math.round(Math.abs(stats.meanErrorMs)),
  })

  return (
    <section className={styles.summary} aria-label="Session complete">
      <h2 className={styles.title}>Session complete</h2>
      <p className={styles.score}>{`${stats.meanPercent}%`}</p>

      {stats.scored < stats.total ? (
        <p className={styles.scored}>
          {`Scored on ${stats.scored} of ${stats.total} — the rest were not heard.`}
        </p>
      ) : null}

      {tendency ? (
        <p className={styles.tendency}>
          <TheoryGlyphs>{tendency.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="large" onClick={onRestart}>
          Practise again
        </Button>
        {onClose ? (
          <Button variant="outlined" size="large" onClick={onClose}>
            Done
          </Button>
        ) : null}
      </div>
    </section>
  )
}
